import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  Checkroom as CheckroomIcon,
  GridView as GridViewIcon,
  Headphones as HeadphonesIcon,
  Inventory2 as Inventory2Icon,
  Kitchen as KitchenIcon,
  Laptop as LaptopIcon,
  LocalAtm as LocalAtmIcon,
  Nfc as NfcIcon,
  Payments as PaymentsIcon,
  Print as PrintIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Remove as RemoveIcon,
  Restaurant as RestaurantIcon,
  RestartAlt as RestartAltIcon,
  Search as SearchIcon,
  ShoppingBag as ShoppingBagIcon,
  Smartphone as SmartphoneIcon,
  Watch as WatchIcon,
} from "@mui/icons-material";
import { branchAPI, salesPointAPI, saleAPI } from "../../services";
import { cardHolderAPI, CardHolder } from "../../services/cardHolderAPI";
import customerAPI, { Customer } from "../../services/customerAPI";
import {
  productSalesPointAPI,
  ProductSalesPointItem,
} from "../../services/productSalesPointAPI";
import { nfcCardAPI } from "../../services/nfcCardAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import AppLogo from "../../components/brand/AppLogo";
import { categoryIconColor, dreamsPos } from "./posDreamsTheme";
import { getApiErrorMessage } from "../../utils/apiError";
import { sumCartGst } from "../../utils/gstTax";

interface CartItem {
  productMasterId: string;
  name: string;
  price: number;
  quantity: number;
  barcode?: string | null;
  category?: string;
  gstPercentage?: number;
  isTaxInclusive?: boolean;
}

type PaymentMethod = "cash" | "upi" | "card";
type CustomerType = "walkin" | "nfc_holder" | "b2b";

interface CustomerDetails {
  name: string;
  phone: string;
  email: string;
}

const categoryIcons: Record<string, React.ElementType> = {
  All: GridViewIcon,
  Headset: HeadphonesIcon,
  Headphones: HeadphonesIcon,
  Shoes: CheckroomIcon,
  Mobiles: SmartphoneIcon,
  Watches: WatchIcon,
  Laptops: LaptopIcon,
  Laptop: LaptopIcon,
  Computer: LaptopIcon,
  Appliance: KitchenIcon,
};

const getCategoryIcon = (name: string) =>
  categoryIcons[name] || CategoryIcon;

const toAmount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const RetailPosPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { hasAnyRole, canChangePosScope } = useRBAC();
  const isMerchant = hasAnyRole("MERCHANT");
  const canChangeScope = canChangePosScope();

  const [selectedBranch, setSelectedBranch] = useState(user?.branchId ?? "");
  const [selectedSalesPoint, setSelectedSalesPoint] = useState(
    user?.salesPointId ?? ""
  );
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannerActive, setScannerActive] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    isMerchant ? "card" : "cash"
  );
  const scannedCardRef = useRef("");
  const [scanBuffer, setScanBuffer] = useState("");
  const [cardInfo, setCardInfo] = useState<{
    displayNumber: string;
    balance: number;
    holderName?: string;
  } | null>(null);
  const [cardScanLoading, setCardScanLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [orderNumber] = useState(() => `#ORD${Date.now().toString().slice(-6)}`);
  const [customerType, setCustomerType] = useState<CustomerType>("walkin");
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    name: "",
    phone: "",
    email: "",
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedHolder, setSelectedHolder] = useState<CardHolder | null>(null);
  const [selectedB2bCustomer, setSelectedB2bCustomer] = useState<Customer | null>(
    null
  );
  const [b2bSearch, setB2bSearch] = useState("");
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error" | "info";
  } | null>(null);
  const [clock, setClock] = useState("");
  const barcodeRef = useRef<HTMLInputElement>(null);

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
    enabled: canChangeScope || !!user?.branchId,
  });

  const { data: salesPoints = [], isLoading: salesPointsLoading } = useQuery({
    queryKey: ["sales_points", selectedBranch],
    queryFn: () =>
      selectedBranch
        ? salesPointAPI.getSalesPointsByBranch(selectedBranch)
        : [],
    enabled: !!selectedBranch,
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["pos-products", selectedSalesPoint],
    queryFn: () =>
      productSalesPointAPI.listBySalesPoint(selectedSalesPoint, {
        pageSize: 500,
      }),
    enabled: !!selectedSalesPoint,
  });

  const { data: holderSearchData, isLoading: holdersLoading } = useQuery({
    queryKey: ["pos-customer-search", customerSearch, selectedBranch],
    queryFn: () =>
      cardHolderAPI.getCardHolders({
        search: customerSearch,
        branchId: selectedBranch || undefined,
        pageSize: 20,
      }),
    enabled: customerType === "nfc_holder" && customerSearch.trim().length >= 2,
  });

  const { data: b2bCustomerData, isLoading: b2bLoading } = useQuery({
    queryKey: ["pos-b2b-customer", b2bSearch],
    queryFn: () =>
      customerAPI.getCustomers({
        search: b2bSearch,
        pageSize: 20,
        pageNo: 1,
      }),
    enabled: customerType === "b2b" && b2bSearch.trim().length >= 2,
  });

  const holderOptions = holderSearchData?.items ?? [];
  const b2bOptions = b2bCustomerData?.data ?? [];

  const products = productsData?.data ?? [];

  const categories = useMemo(() => {
    const names = products.map((p) => p.category?.name || "General");
    return ["All", ...Array.from(new Set(names))];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCategory !== "All") {
      list = list.filter(
        (p) => (p.category?.name || "General") === selectedCategory
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.barcode || "").toLowerCase().includes(q) ||
          p.sku.toLowerCase().includes(q)
      );
    }
    return list;
  }, [products, selectedCategory, search]);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const activeTaxMode =
    salesPoints.find((d) => d.id === selectedSalesPoint)?.taxMode ||
    "CGST_SGST";
  const gstTotals = useMemo(
    () =>
      sumCartGst(
        cartItems.map((item) => ({
          lineNet: item.price * item.quantity,
          gstPercentage: item.gstPercentage ?? 0,
          isTaxInclusive: Boolean(item.isTaxInclusive),
          taxMode: activeTaxMode,
        }))
      ),
    [cartItems, activeTaxMode]
  );
  const taxAmount = gstTotals.taxAmount;
  const discountAmount = 0;
  const shippingAmount = 0;
  const totalAmount = gstTotals.netAmount + shippingAmount;
  const totalQty = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const branchName =
    branches.find((b) => b.id === selectedBranch)?.name || "Select Branch";
  const deptName =
    salesPoints.find((d) => d.id === selectedSalesPoint)?.name ||
    "Select SalesPoint";
  const welcomeName = user?.name || "Cashier";

  useEffect(() => {
    const tick = () => {
      setClock(
        new Date().toLocaleString("en-IN", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!canChangeScope && user) {
      if (user.branchId) setSelectedBranch(user.branchId);
      if (user.salesPointId) setSelectedSalesPoint(user.salesPointId);
    }
  }, [canChangeScope, user?.branchId, user?.salesPointId]);

  useEffect(() => {
    if (scannerActive) barcodeRef.current?.focus();
  }, [scannerActive, cartItems.length]);

  const addProduct = (product: ProductSalesPointItem) => {
    setCartItems((prev) => {
      const existing = prev.find(
        (i) => i.productMasterId === product.productMasterId
      );
      if (existing) {
        return prev.map((i) =>
          i.productMasterId === product.productMasterId
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [
        ...prev,
        {
          productMasterId: product.productMasterId,
          name: product.name,
          price: product.sellingPrice,
          quantity: 1,
          barcode: product.barcode,
          category: product.category?.name || "General",
          gstPercentage: Number(product.gstPercentage) || 0,
          isTaxInclusive: Boolean(product.isTaxInclusive),
        },
      ];
    });
  };

  const addByBarcode = (code: string) => {
    const product = products.find((p) => p.barcode === code.trim());
    if (product) addProduct(product);
    else setToast({ message: "Product not found", severity: "error" });
  };

  const updateQty = (productMasterId: string, delta: number) => {
    setCartItems((prev) =>
      prev
        .map((item) =>
          item.productMasterId === productMasterId
            ? { ...item, quantity: item.quantity + delta }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const clearCart = () => setCartItems([]);

  const resetCardPayment = () => {
    scannedCardRef.current = "";
    setScanBuffer("");
    setCardInfo(null);
    setCardScanLoading(false);
  };

  const resetCustomer = () => {
    setCustomerDetails({ name: "", phone: "", email: "" });
    setSelectedCustomerId(null);
    setSelectedHolder(null);
    setSelectedB2bCustomer(null);
    setCustomerSearch("");
    setB2bSearch("");
    setCustomerType("walkin");
  };

  const buildOrderNotes = () => {
    const parts: string[] = [];
    if (customerDetails.phone.trim()) {
      parts.push(`Phone: ${customerDetails.phone.trim()}`);
    }
    if (customerDetails.email.trim()) {
      parts.push(`Email: ${customerDetails.email.trim()}`);
    }
    return parts.length > 0 ? parts.join(" | ") : undefined;
  };

  const applyB2bCustomer = (customer: Customer) => {
    setSelectedB2bCustomer(customer);
    setSelectedCustomerId(customer.id);
    setSelectedHolder(null);
    setCustomerDetails({
      name: customer.name,
      phone: customer.phone || "",
      email: customer.email || "",
    });
  };

  const applyHolder = (holder: CardHolder) => {
    setSelectedHolder(holder);
    setSelectedB2bCustomer(null);
    setSelectedCustomerId(holder.id);
    setCustomerDetails({
      name: holder.holderName,
      phone: holder.phone,
      email: holder.email || "",
    });
  };

  const handleCustomerTypeChange = (type: CustomerType) => {
    setCustomerType(type);
    setSelectedHolder(null);
    setSelectedB2bCustomer(null);
    setSelectedCustomerId(null);
    setCustomerSearch("");
    setB2bSearch("");
    if (type === "walkin") {
      setCustomerDetails({ name: "", phone: "", email: "" });
    }
  };

  const handleCardScan = async (number: string) => {
    scannedCardRef.current = number;
    setScanBuffer("");
    setCardInfo(null);
    setCardScanLoading(true);
    try {
      const card = await nfcCardAPI.getCardByNumber(number);
      setCardInfo({
        displayNumber: card.displayNumber,
        balance: toAmount(card.balance),
        holderName: card.holder?.holderName,
      });
      if (card.holder) {
        applyHolder(card.holder as CardHolder);
        setCustomerType("nfc_holder");
      } else if (card.holderId) {
        setSelectedCustomerId(card.holderId);
      }
    } catch {
      scannedCardRef.current = "";
      setCardInfo(null);
      setToast({ message: "Card not found", severity: "error" });
    } finally {
      setCardScanLoading(false);
    }
  };

  const validateCustomer = (): boolean => {
    const name = customerDetails.name.trim();
    const phone = customerDetails.phone.trim();

    if (!name) {
      setToast({ message: "Customer name is required", severity: "error" });
      return false;
    }
    if (!phone || phone.length < 10) {
      setToast({ message: "Valid phone number is required", severity: "error" });
      return false;
    }
    if (
      customerDetails.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email.trim())
    ) {
      setToast({ message: "Enter a valid email address", severity: "error" });
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      setToast({ message: "Add products to place order", severity: "error" });
      return;
    }
    if (!selectedBranch || !selectedSalesPoint) {
      setToast({ message: "Select branch and salesPoint", severity: "error" });
      return;
    }
    if (!validateCustomer()) return;
    if (paymentMethod === "card" && !scannedCardRef.current.trim()) {
      setToast({ message: "Scan NFC card for payment", severity: "error" });
      return;
    }
    if (
      paymentMethod === "card" &&
      cardInfo &&
      cardInfo.balance < totalAmount
    ) {
      setToast({
        message: `Insufficient card balance. Available: ₹${cardInfo.balance.toFixed(2)}, Required: ₹${totalAmount.toFixed(2)}`,
        severity: "error",
      });
      return;
    }

    setCheckingOut(true);
    try {
      await saleAPI.syncCompleteSale({
        saleNumber: `POS-${Date.now()}`,
        branchId: selectedBranch,
        salesPointId: selectedSalesPoint,
        paymentMethod,
        totalAmount: subtotal,
        taxAmount,
        discountAmount,
        netAmount: totalAmount,
        status: "completed",
        notes: `Retail POS ${orderNumber}`,
        customerName: customerDetails.name.trim(),
        ...(customerType === "b2b" && selectedCustomerId
          ? { customerId: selectedCustomerId }
          : {}),
        orderNotes: buildOrderNotes(),
        ...(paymentMethod === "card"
          ? { cardNumber: scannedCardRef.current.trim() }
          : {}),
        items: cartItems.map((item) => ({
          productId: item.productMasterId,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.price * item.quantity,
          netAmount: item.price * item.quantity,
        })),
      });

      setToast({
        message: `Order placed — ₹${totalAmount.toFixed(2)}`,
        severity: "success",
      });
      clearCart();
      resetCustomer();
      resetCardPayment();
    } catch (err: unknown) {
      setToast({
        message: getApiErrorMessage(err, "Order failed"),
        severity: "error",
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const paymentOptions: {
    id: PaymentMethod;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { id: "cash", label: "Cash", icon: <LocalAtmIcon /> },
    { id: "card", label: "NFC Card", icon: <NfcIcon /> },
    { id: "upi", label: "UPI", icon: <PaymentsIcon /> },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: dreamsPos.bg,
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      {/* DreamsPOS top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          bgcolor: dreamsPos.panel,
          borderBottom: `1px solid ${dreamsPos.border}`,
          flexShrink: 0,
        }}
      >
        <AppLogo size="sm" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} color={dreamsPos.text}>
            POS
          </Typography>
          <Typography variant="caption" color={dreamsPos.textMuted}>
            {branchName} · {deptName}
          </Typography>
        </Box>

        <Typography
          variant="caption"
          sx={{ display: { xs: "none", md: "block" }, color: dreamsPos.textMuted }}
        >
          {clock}
        </Typography>

        {canChangeScope ? (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: 120, display: { xs: "none", sm: "block" } }}
            >
              <Select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setSelectedSalesPoint("");
                }}
                disabled={branchesLoading}
                displayEmpty
                sx={{ fontSize: "0.8125rem", bgcolor: dreamsPos.bg }}
              >
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: 130, display: { xs: "none", sm: "block" } }}
            >
              <Select
                value={selectedSalesPoint}
                onChange={(e) => setSelectedSalesPoint(e.target.value)}
                disabled={!selectedBranch || salesPointsLoading}
                displayEmpty
                sx={{ fontSize: "0.8125rem", bgcolor: dreamsPos.bg }}
              >
                {salesPoints.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Chip
            label={`${branchName} · ${deptName}`}
            size="small"
            sx={{
              display: { xs: "none", sm: "flex" },
              bgcolor: dreamsPos.bg,
              fontWeight: 600,
              maxWidth: 280,
            }}
          />
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<RestaurantIcon />}
          onClick={() => navigate("/sales/restaurant")}
          sx={{ borderColor: dreamsPos.border, color: dreamsPos.text }}
        >
          Restaurant
        </Button>
        <IconButton size="small" onClick={() => navigate("/dashboard")}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Left category rail — DreamsPOS style */}
        <Box
          sx={{
            width: 88,
            flexShrink: 0,
            bgcolor: dreamsPos.panel,
            borderRight: `1px solid ${dreamsPos.border}`,
            overflowY: "auto",
            py: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {categories.map((cat, idx) => {
            const Icon = getCategoryIcon(cat);
            const active = selectedCategory === cat;
            return (
              <Box
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                sx={{
                  width: 72,
                  py: 1,
                  px: 0.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  textAlign: "center",
                  bgcolor: active ? categoryIconColor(idx) : "transparent",
                  border: active
                    ? `2px solid ${dreamsPos.orange}`
                    : "2px solid transparent",
                  transition: "all 0.15s",
                  "&:hover": { bgcolor: categoryIconColor(idx) },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: categoryIconColor(idx),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 0.5,
                    color: dreamsPos.navy,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: active ? 700 : 500,
                    color: active ? dreamsPos.orangeDark : dreamsPos.textMuted,
                    lineHeight: 1.2,
                    display: "block",
                  }}
                >
                  {cat}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Center — products */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: dreamsPos.panel,
              borderBottom: `1px solid ${dreamsPos.border}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700} color={dreamsPos.text}>
                  Welcome, {welcomeName}
                </Typography>
                <Typography variant="caption" color={dreamsPos.textMuted}>
                  {new Date().toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label="Featured"
                  size="small"
                  onClick={() => setSelectedCategory("All")}
                  sx={{
                    bgcolor:
                      selectedCategory === "All" ? dreamsPos.orange : dreamsPos.bg,
                    color: selectedCategory === "All" ? "#fff" : dreamsPos.text,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="All Brands"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedCategory("All");
                    setSearch("");
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, bgcolor: dreamsPos.bg }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (barcodeInput.trim()) {
                    addByBarcode(barcodeInput.trim());
                    setBarcodeInput("");
                  }
                }}
                style={{ flex: 1 }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Scan barcode..."
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  disabled={!scannerActive || !selectedSalesPoint}
                  inputRef={barcodeRef}
                  sx={{ bgcolor: dreamsPos.bg }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QrCodeScannerIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </form>
              <Chip
                icon={<QrCodeScannerIcon />}
                label={scannerActive ? "Scan ON" : "Scan OFF"}
                onClick={() => setScannerActive(!scannerActive)}
                sx={{
                  cursor: "pointer",
                  bgcolor: scannerActive ? dreamsPos.green : dreamsPos.bg,
                  color: scannerActive ? "#fff" : dreamsPos.text,
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {!selectedSalesPoint ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Inventory2Icon sx={{ fontSize: 56, color: dreamsPos.border, mb: 1 }} />
                <Typography color={dreamsPos.textMuted}>
                  Select branch & salesPoint to load products
                </Typography>
              </Box>
            ) : productsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: dreamsPos.orange }} />
              </Box>
            ) : filteredProducts.length === 0 ? (
              <Typography align="center" color={dreamsPos.textMuted} sx={{ py: 8 }}>
                No products in this category
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                    xl: "repeat(5, 1fr)",
                  },
                  gap: 1.5,
                }}
              >
                {filteredProducts.map((product) => (
                  <Box
                    key={product.id}
                    onClick={() => addProduct(product)}
                    sx={{
                      bgcolor: dreamsPos.panel,
                      border: `1px solid ${dreamsPos.border}`,
                      borderRadius: 2,
                      p: 1.5,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: dreamsPos.orange,
                        boxShadow: "0 8px 24px rgba(255,159,67,0.12)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 100,
                        borderRadius: 1.5,
                        bgcolor: categoryIconColor(
                          categories.indexOf(product.category?.name || "General")
                        ),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <ShoppingBagIcon sx={{ fontSize: 36, color: dreamsPos.navy, opacity: 0.35 }} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: dreamsPos.orange, fontWeight: 600, textTransform: "uppercase" }}
                    >
                      {product.category?.name || "Products"}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      noWrap
                      title={product.name}
                      color={dreamsPos.text}
                    >
                      {product.name}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      sx={{ color: dreamsPos.green, mt: 0.5 }}
                    >
                      ₹{product.sellingPrice.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right — order panel (DreamsPOS Order List) */}
        <Box
          sx={{
            width: { xs: 340, md: 420 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: dreamsPos.panel,
            borderLeft: `1px solid ${dreamsPos.border}`,
            overflow: "hidden",
            minHeight: 0,
            height: "100%",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${dreamsPos.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" fontWeight={700} color={dreamsPos.text}>
              Order List
            </Typography>
            <Chip
              label={orderNumber}
              size="small"
              sx={{ bgcolor: dreamsPos.bg, fontWeight: 700 }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${dreamsPos.border}` }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Customer Information
            </Typography>
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <Select
                value={customerType}
                onChange={(e) =>
                  handleCustomerTypeChange(e.target.value as CustomerType)
                }
                sx={{ bgcolor: dreamsPos.bg, fontSize: "0.875rem" }}
              >
                <MenuItem value="walkin">Walk in Customer</MenuItem>
                <MenuItem value="nfc_holder">NFC Card Holder</MenuItem>
                <MenuItem value="b2b">B2B Customer</MenuItem>
              </Select>
            </FormControl>

            {customerType === "nfc_holder" && (
              <Autocomplete
                size="small"
                options={holderOptions}
                loading={holdersLoading}
                value={selectedHolder}
                onChange={(_, holder) => {
                  if (holder) applyHolder(holder);
                  else {
                    setSelectedHolder(null);
                    setSelectedCustomerId(null);
                  }
                }}
                inputValue={customerSearch}
                onInputChange={(_, value) => setCustomerSearch(value)}
                getOptionLabel={(o) =>
                  `${o.holderName} · ${o.phone}${o.email ? ` · ${o.email}` : ""}`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search NFC holder by name or phone..."
                    sx={{ mb: 1, bgcolor: dreamsPos.bg }}
                  />
                )}
                noOptionsText={
                  customerSearch.trim().length < 2
                    ? "Type 2+ characters to search"
                    : "No holders found"
                }
              />
            )}

            {customerType === "b2b" && (
              <Autocomplete
                size="small"
                options={b2bOptions}
                loading={b2bLoading}
                value={selectedB2bCustomer}
                onChange={(_, customer) => {
                  if (customer) applyB2bCustomer(customer);
                  else {
                    setSelectedB2bCustomer(null);
                    setSelectedCustomerId(null);
                  }
                }}
                inputValue={b2bSearch}
                onInputChange={(_, value) => setB2bSearch(value)}
                getOptionLabel={(o) =>
                  `${o.name}${o.gstin ? ` · ${o.gstin}` : ""}${o.phone ? ` · ${o.phone}` : ""}`
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search B2B customer (name/GSTIN)..."
                    sx={{ mb: 1, bgcolor: dreamsPos.bg }}
                  />
                )}
                noOptionsText={
                  b2bSearch.trim().length < 2
                    ? "Type 2+ characters to search"
                    : "No customers found"
                }
              />
            )}

            <TextField
              size="small"
              fullWidth
              required
              label="Customer Name"
              value={customerDetails.name}
              onChange={(e) =>
                setCustomerDetails((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ mb: 1, bgcolor: dreamsPos.bg }}
            />
            <TextField
              size="small"
              fullWidth
              required
              label="Phone Number"
              value={customerDetails.phone}
              onChange={(e) =>
                setCustomerDetails((prev) => ({
                  ...prev,
                  phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                }))
              }
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
              sx={{ mb: 1, bgcolor: dreamsPos.bg }}
            />
            <TextField
              size="small"
              fullWidth
              label="Email (Optional)"
              type="email"
              value={customerDetails.email}
              onChange={(e) =>
                setCustomerDetails((prev) => ({ ...prev, email: e.target.value }))
              }
              sx={{ bgcolor: dreamsPos.bg }}
            />
          </Box>

          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${dreamsPos.border}` }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Order Details
              </Typography>
              <Typography variant="caption" color={dreamsPos.textMuted}>
                Items: {totalQty}
              </Typography>
            </Box>
            {cartItems.length > 0 && (
              <Button size="small" color="error" onClick={clearCart} sx={{ mt: 0.5, p: 0 }}>
                Clear all
              </Button>
            )}
          </Box>

          <Box sx={{ px: 2, pb: 1 }}>
            {cartItems.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
                <ShoppingBagIcon sx={{ fontSize: 48, color: dreamsPos.border, mb: 1 }} />
                <Typography variant="body2" color={dreamsPos.textMuted}>
                  No Products Selected
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: dreamsPos.bg }}>Item</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: dreamsPos.bg, width: 90 }}>
                      QTY
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: dreamsPos.bg, width: 80 }}>
                      Cost
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartItems.map((item) => (
                    <TableRow key={item.productMasterId} hover>
                      <TableCell sx={{ maxWidth: 140 }}>
                        <Typography variant="caption" fontWeight={600} noWrap display="block">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color={dreamsPos.textMuted}>
                          ₹{item.price.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                          <IconButton size="small" onClick={() => updateQty(item.productMasterId, -1)}>
                            <RemoveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Typography variant="body2" fontWeight={700} sx={{ mx: 0.5 }}>
                            {item.quantity}
                          </Typography>
                          <IconButton size="small" onClick={() => updateQty(item.productMasterId, 1)}>
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color={dreamsPos.green}>
                          ₹{(item.price * item.quantity).toFixed(0)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
          </Box>

          {/* Payment summary */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${dreamsPos.border}`,
              bgcolor: dreamsPos.bg,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Payment Summary
            </Typography>
            {[
              ["Sub Total", subtotal],
              [
                activeTaxMode === "IGST" ? "IGST" : "CGST+SGST",
                taxAmount,
              ],
              ["Discount", discountAmount],
              ["Shipping", shippingAmount],
            ].map(([label, val]) => (
              <Box
                key={label as string}
                sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}
              >
                <Typography variant="caption" color={dreamsPos.textMuted}>
                  {label}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  ₹{Number(val).toFixed(2)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" fontWeight={800}>
                Total Payable
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} color={dreamsPos.orangeDark}>
                ₹{totalAmount.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Payment method tiles */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${dreamsPos.border}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Select Payment
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
                mb: paymentMethod === "card" ? 1.5 : 0,
              }}
            >
              {(isMerchant ? paymentOptions.filter((p) => p.id === "card") : paymentOptions).map(
                (opt) => (
                  <Box
                    key={opt.id}
                    onClick={() => setPaymentMethod(opt.id)}
                    sx={{
                      border: `2px solid ${
                        paymentMethod === opt.id ? dreamsPos.orange : dreamsPos.border
                      }`,
                      borderRadius: 1.5,
                      py: 1,
                      textAlign: "center",
                      cursor: "pointer",
                      bgcolor:
                        paymentMethod === opt.id ? "#fff8f0" : dreamsPos.panel,
                      color: paymentMethod === opt.id ? dreamsPos.orangeDark : dreamsPos.text,
                      "& svg": { display: "block", mx: "auto", mb: 0.5 },
                    }}
                  >
                    {opt.icon}
                    <Typography variant="caption" fontWeight={700}>
                      {opt.label}
                    </Typography>
                  </Box>
                )
              )}
            </Box>

            {paymentMethod === "card" && (
              <Box sx={{ mt: 1 }}>
                <NfcCardScanInput
                  value={scanBuffer}
                  onChange={setScanBuffer}
                  onScanComplete={handleCardScan}
                  hideScannedValue
                  hideInputAfterScan
                  loading={cardScanLoading}
                  resolvedDisplayNumber={cardInfo?.displayNumber}
                  allowRescan
                  onClear={resetCardPayment}
                  label="NFC Card"
                  hint="Tap card on reader"
                />
                {cardInfo && (
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    {cardInfo.holderName && (
                      <Typography variant="body2">{cardInfo.holderName}</Typography>
                    )}
                    <Typography variant="body2">
                      Balance ₹{cardInfo.balance.toFixed(2)}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          {/* Action buttons — DreamsPOS style */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${dreamsPos.border}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={cartItems.length === 0}
                sx={{ borderColor: dreamsPos.border, color: dreamsPos.text }}
              >
                Print Order
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handlePlaceOrder}
                disabled={cartItems.length === 0 || checkingOut}
                sx={{
                  bgcolor: dreamsPos.green,
                  "&:hover": { bgcolor: dreamsPos.greenDark },
                  fontWeight: 700,
                }}
              >
                {checkingOut ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Place Order"
                )}
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              <Button size="small" variant="text" color="inherit" onClick={clearCart}>
                Reset
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  clearCart();
                  resetCustomer();
                  resetCardPayment();
                }}
              >
                Transaction
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {toast && (
        <Snackbar
          open
          autoHideDuration={3000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default RetailPosPage;
